import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import DecisionRadio from './DecisionRadio';
import GoalCheckInHistory from './GoalCheckInHistory';
import GoalTypeDetails from './GoalTypeDetails';

// Task 27 — "Onay Bekleyenler" sekmesi karar modalı: hedef detayını (GET
// v1/period-goals/{id} — KR'ler + ilerleme) gösterir, ardından onay/revizyon
// radio + gerekçe (revizyonda FE + BE ikisi de zorunlu kılıyor — BE reddi
// "performance.goal.revisionNoteRequired", responseErrors'ta zaten mevcut).
// isHr true iken hr-decision, false iken manager-decision ucuna gönderir —
// GoalStateMachine'in hangi durumdan geldiğine göre servis tarafında otomatik
// seçilir (bu modal yalnız hangi ucu çağıracağını bilir).
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1400;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 640px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const DialogTitle = styled.h3`
  margin: 0 0 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 10px;
  border-bottom: 1px solid #E8ECF0;
  padding-bottom: 6px;
`;

const ReadonlyValue = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 4px 0;
`;

const KrRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  background: #F5F7FA;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 6px;
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const HintText = styled.div`
  font-size: 12px;
  color: #b02a37;
  margin-top: 4px;
`;

const EmptyBox = styled.div`
  padding: 20px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

// P3 (2026-10-08) — kilometre taşı/eşik hedeflerinin ayrıntısı GoalTypeDetails ile; yönetici
// detayında (canDecideSteps) kabul bekleyen adımlar burada kabul/ret edilir (decideMilestoneStep).
export default function GoalDecisionModal({
  open, goalId, isHr, readOnly, onClose, onDecided, getById, managerDecide, hrDecide,
  canDecideSteps = false, decideMilestoneStep,
}) {
  const { t } = useTranslation();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState('approve');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (!open || !goalId) { setGoal(null); return; }
    setDecision('approve');
    setNote('');
    setLoading(true);
    getById(goalId).then((g) => setGoal(g)).finally(() => setLoading(false));
  }, [open, goalId, getById, reloadKey]);
  const reloadGoal = useCallback(() => setReloadKey((k) => k + 1), []);

  const noteRequired = decision === 'revision';
  const canSubmit = !noteRequired || note.trim().length > 0;

  const handleSubmit = useCallback(async () => {
    if (!goalId || !canSubmit) return;
    setBusy(true);
    const decide = isHr ? hrDecide : managerDecide;
    const ok = await decide(goalId, decision, note.trim() || undefined);
    setBusy(false);
    if (ok) onDecided();
  }, [goalId, canSubmit, isHr, hrDecide, managerDecide, decision, note, onDecided]);

  if (!open) return null;

  return (
    <Modal>
      <ModalCard>
        <DialogTitle>
          {readOnly ? t('performance.teamGoals.actions.viewDetail') : t('performance.teamGoals.decision.title')}
        </DialogTitle>

        {loading && <EmptyBox>{t('common.loading')}</EmptyBox>}

        {!loading && goal && (
          <>
            <ReadonlyValue><strong>{goal.employeeName}</strong> — {goal.title}</ReadonlyValue>
            {goal.description && <ReadonlyValue>{goal.description}</ReadonlyValue>}
            {readOnly && (
              <ReadonlyValue>
                {t('common.status')}: {t(`performance.goal.status.${goal.status}`, { defaultValue: goal.status })}
              </ReadonlyValue>
            )}

            {goal.goalType === 'okr' && (
              <>
                <SectionTitle>{t('performance.goal.keyResults')}</SectionTitle>
                {(goal.keyResults || []).map((kr) => (
                  <KrRow key={kr.id}>
                    <span>{kr.title}</span>
                    <span>{kr.progressPct}%</span>
                    {readOnly && (kr.employeeScore != null || kr.managerScore != null) && (
                      <span>
                        {t('performance.goal.employeeScore')}: {kr.employeeScore ?? '-'} / {t('performance.goal.managerScore')}: {kr.managerScore ?? '-'}
                      </span>
                    )}
                  </KrRow>
                ))}
              </>
            )}
            {/* P3 — kilometre taşı (adımlar + yöneticinin kabul/reddi) ve eşik (koşul + sonuç). */}
            <GoalTypeDetails
              goal={goal}
              mode={canDecideSteps ? 'manager' : 'readonly'}
              canAct={canDecideSteps && ['approved', 'evaluating'].includes(goal.status)}
              onDecideStep={decideMilestoneStep}
              onChanged={reloadGoal}
            />
            {goal.goalType === 'kpi' && (
              <ReadonlyValue>
                {goal.kpiIndicatorName} — {t('performance.goal.kpiTargetValue')}: {goal.kpiTargetValue} {goal.kpiUnit ? t(`performance.goal.kpiUnit.${goal.kpiUnit}`, { defaultValue: goal.kpiUnit }) : ''}
                {goal.kpiDirection ? ` · ${t(`performance.goal.kpiDirection.${goal.kpiDirection}`)}` : ''}
                {/* Müşteri önerisi (2026-08-10): tamamlanma %'sinin yanında HAM gerçekleşen
                    de görünsün — yalnız doluyken (onay aşamasında satır sade kalır). */}
                {goal.kpiActualValue != null ? ` · ${t('performance.goal.kpiActualValue')}: ${goal.kpiActualValue}` : ''}
                {goal.completionPct != null ? ` (${t('performance.goal.kpiCompletionPct')}: ${goal.completionPct}%)` : ''}
              </ReadonlyValue>
            )}

            {/* P1 — çalışanın dönem-sonu sapma gerekçesi (varsa; onay aşamasında
                henüz girilmemiş olur, dönem-sonu/karar görünümünde dolu gelir). */}
            {goal.deviationRationale && (
              <ReadonlyValue>
                <strong>{t('performance.goal.deviationRationale')}:</strong> {goal.deviationRationale}
              </ReadonlyValue>
            )}

            {!readOnly && (
              <>
                <SectionTitle>{t('performance.teamGoals.decision.title')}</SectionTitle>
                <FieldGroup>
                  <DecisionRadio
                    name="goal-decision"
                    value={decision}
                    onChange={setDecision}
                    options={[
                      { value: 'approve', label: t('performance.teamGoals.decision.approve') },
                      { value: 'revision', label: t('performance.teamGoals.decision.sendBackForRevision') },
                    ]}
                  />
                </FieldGroup>
                <FieldGroup>
                  <TextInput
                    label={t('performance.teamGoals.decision.note')}
                    type="textarea"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    required={noteRequired}
                  />
                  {noteRequired && !canSubmit && <HintText>{t('performance.teamGoals.decision.noteRequiredHint')}</HintText>}
                </FieldGroup>
              </>
            )}

            {/* Skorlama Faz F, Task 5 (task-1-report.md §3) — sahip-dışı
                ("ekip"/İK) görünümde geçmiş, GetCheckInsAsync'in doğrudan
                yönetici/İK kısıtlı görünürlüğüyle çekilir; kalibrasyon gibi
                DAHA GENİŞ bir PM.Read ile buraya gelmiş ama doğrudan yönetici/
                İK OLMAYAN bir görüntüleyicide 403 mümkün — component bunu
                kendi içinde toast'sız yutar, bölüm o durumda hiç görünmez
                (bkz. GoalCheckInHistory.js). Karar akışında da (readOnly=false)
                gösterilir — yöneticinin/İK'nın karar vereceği bağlamda geçmişi
                görmesi faydalı, uç zaten AYNI (CheckManagerScopeAsync). */}
            <GoalCheckInHistory goalId={goal.id} apiPath={`/v1/period-goals/${goal.id}/check-ins`} />
          </>
        )}

        {!loading && !goal && <EmptyBox>{t('performance.teamGoals.notFound')}</EmptyBox>}

        <Actions>
          <Button variant="outline" onClick={onClose}>
            {readOnly ? t('common.close') : t('common.cancel')}
          </Button>
          {!readOnly && (
            <Button onClick={handleSubmit} disabled={!goal || !canSubmit || busy} loading={busy}>
              {t('performance.teamGoals.decision.submit')}
            </Button>
          )}
        </Actions>
      </ModalCard>
    </Modal>
  );
}
